import {
  INTAKE_OTHER_MAX,
  LEFT_IN_CAR,
  SIGNED_CONTENT_VERSION,
  cleanIntakeExtras,
  parseKeyCount,
  type IntakeExtras,
  type LeftInCar,
  buildReadySms,
  siteAddressOf,
  INTAKE_BASE_VIEWS,
  cleanObjection,
  contentHash,
  formatPlate,
  intakeSettings,
  intakeNeeds,
  odometerFromInput,
  odometerWarning,
  missingBaseViews,
  nextIntakeView,
  signedContent,
  shortHash,
  hasDriverContact,
  recipientDefaults,
  type IntakeView,
  type SignatureKind,
  type SignedContent,
} from "@szerviz/core";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useApp, useCan, useFeature } from "../context";
import type { Customer, Vehicle, IntakeLink, IntakeObjection, IntakeObjectionNotice, Photo, WorkOrder, WorkOrderSignature } from "../data";
import { shrinkPhoto } from "../photo";
import { ErrorText } from "./ErrorText";
import { Help } from "./Help";
import { ChannelSend, RecipientsSend, smsNumberOf } from "./ChannelSend";
import { Icon } from "./Icon";
import { KeyValues } from "./PrintDocs";
import { PlanTag } from "./TrialGuide";
import { TextInput } from "./Field";

/**
 * Which device the program runs on, for what opens by itself (owner's bug report, 2026-11-04): the signature pad
 * opens by itself only on a touch phone or tablet (primary pointer coarse, no hover); the intake receipt prints by
 * itself only on a computer (primary pointer fine, with hover). A touch screen laptop counts as a computer (its
 * primary pointer is the touchpad or mouse): the pad stays one tap away there. When the browser cannot tell
 * (no matchMedia), nothing opens by itself.
 */
export const TOUCH_QUERY = "(pointer: coarse) and (hover: none)";
export const COMPUTER_QUERY = "(pointer: fine) and (hover: hover)";
export type DeviceKind = "touch" | "computer" | "unknown";
export function deviceKind(): DeviceKind {
  const m = (q: string) => globalThis.matchMedia?.(q).matches ?? false;
  return m(TOUCH_QUERY) ? "touch" : m(COMPUTER_QUERY) ? "computer" : "unknown";
}

/**
 * Intake with photos and the signature on the phone (features "intakePhotos" and "phoneSignature",
 * owner's design of 2026-11-04): the thumbnails of the round, the guided capture, the signature pad,
 * the panel on the work order, the settings and the corner notice of a customer's objection.
 */

/** One intake photo as the grid shows it (stored, or still on the phone in a new work order). */
export interface IntakeShot {
  id: string;
  view: IntakeView;
  url: string;
  createdAt: string;
}

export const shotsOf = (photos: Photo[]): IntakeShot[] =>
  photos.filter((p) => p.kind === "intake").map((p) => ({ id: p.id, view: p.intakeView ?? "damage", url: p.url, createdAt: p.createdAt }))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));

/** The intake link the customer opens (#intake/<token>). */
export const intakeLink = (token: string) => `${globalThis.location?.origin ?? ""}${globalThis.location?.pathname ?? "/"}#intake/${token}`;

/** The label of a shot: the view, or "Sérülés 2" for the second damage close up. */
export function useShotLabel() {
  const { t } = useTranslation();
  return (shots: IntakeShot[], s: IntakeShot) =>
    s.view === "damage" ? t("intake.view.damageN", { n: shots.filter((x) => x.view === "damage").indexOf(s) + 1 }) : t(`intake.view.${s.view}`);
}

/**
 * The tiles of the round: the five base views (done: the photo with a tick; missing: a dashed tile),
 * the damage close ups, and a "+ Sérülés" tile. A missing tile starts the capture at that view.
 */
export function IntakeGrid({ shots, onStart, onOpen }: { shots: IntakeShot[]; onStart?: (view: IntakeView) => void; onOpen?: (s: IntakeShot) => void }) {
  const { t } = useTranslation();
  const label = useShotLabel();
  const base = INTAKE_BASE_VIEWS.map((v) => ({ v, shot: shots.find((s) => s.view === v) }));
  const damage = shots.filter((s) => s.view === "damage");
  const tile = (s: IntakeShot) => (
    <li key={s.id}>
      <button type="button" className="ig-tile ig-done" onClick={() => onOpen?.(s)} aria-label={label(shots, s)}>
        <img src={s.url} alt="" />
        <span className="ig-label">{label(shots, s)}</span>
        <span className="ig-tick" aria-hidden="true">✓</span>
      </button>
    </li>
  );
  return (
    <ul className="ig-grid">
      {base.map(({ v, shot }) =>
        shot ? tile(shot) : (
          <li key={v}>
            <button type="button" className="ig-tile ig-missing" disabled={!onStart} onClick={() => onStart?.(v)} aria-label={`${t("intake.capture.take")}: ${t(`intake.view.${v}`)}`}>
              <span className="ig-label">{t(`intake.view.${v}`)}</span>
            </button>
          </li>
        ),
      )}
      {damage.map(tile)}
      {onStart && (
        <li>
          <button type="button" className="ig-tile ig-missing ig-add" onClick={() => onStart("damage")}>
            <span className="ig-label">+ {t("intake.view.damage")}</span>
          </button>
        </li>
      )}
    </ul>
  );
}

/**
 * The guided capture on the phone, full screen, text only (owner's decision: no car outline).
 * The camera is the phone's own camera app through a file input with capture="environment", opened again
 * for every photo: it works the same on iOS Safari and Android Chrome, needs no camera permission dialog of
 * the browser, and gives full resolution photos with the right orientation (a live getUserMedia view is
 * fragile on iOS: it stops when the page loses focus, and home screen apps often refuse it).
 */
export function GuidedCapture({ shots, start, onShot, onClose }: {
  shots: IntakeShot[];
  /** Start at this view (a tapped missing tile); otherwise the next missing base view. */
  start?: IntakeView;
  onShot: (view: IntakeView, file: File) => Promise<void>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const label = useShotLabel();
  const input = useRef<HTMLInputElement>(null);
  const [skipped, setSkipped] = useState<IntakeView[]>([]);
  const [override, setOverride] = useState<IntakeView | undefined>(start);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const done = shots.map((s) => s.view);
  const view = override ?? nextIntakeView(done, skipped);
  const baseIndex = INTAKE_BASE_VIEWS.indexOf(view as (typeof INTAKE_BASE_VIEWS)[number]);
  const damageCount = shots.filter((s) => s.view === "damage").length;
  const last = shots[shots.length - 1];

  const take = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await onShot(view, file);
      setOverride(undefined);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const skip = () => {
    setSkipped((s) => [...s, view]);
    setOverride(undefined);
  };

  return (
    <div className="capture" role="dialog" aria-modal="true" aria-label={t("intake.capture.title")}>
      <header className="cap-top">
        <p className="cap-count">
          {baseIndex >= 0 ? t("intake.capture.counter", { n: baseIndex + 1, total: INTAKE_BASE_VIEWS.length }) : t("intake.capture.damageCounter", { n: damageCount + 1 })}
        </p>
        <h2 className="cap-title">{t(`intake.view.${view}`)}</h2>
        <p className="cap-hint">{t(`intake.hint.${view}`)}</p>
        <div className="cap-progress" aria-hidden="true">
          {INTAKE_BASE_VIEWS.map((v) => (
            <i key={v} className={done.includes(v) ? "done" : v === view ? "now" : skipped.includes(v) ? "skipped" : ""} />
          ))}
        </div>
      </header>
      <div className="cap-view">
        {last ? (
          <figure>
            <img src={last.url} alt="" />
            <figcaption>{t("intake.capture.lastDone", { view: label(shots, last), count: shots.length })}</figcaption>
          </figure>
        ) : (
          <p>{t("intake.capture.startHint")}</p>
        )}
        {busy && <p className="cap-busy" role="status">{t("intake.capture.saving")}</p>}
        <ErrorText error={error} />
      </div>
      <footer className="cap-bar">
        {baseIndex >= 0 ? (
          <button type="button" className="cap-side" onClick={skip} disabled={busy}>
            {t("intake.capture.skip")}
          </button>
        ) : (
          <span className="cap-side cap-empty" />
        )}
        <button type="button" className="cap-shutter" onClick={() => input.current?.click()} disabled={busy} aria-label={`${t("intake.capture.take")}: ${t(`intake.view.${view}`)}`}>
          <Icon name="photo" size={34} />
        </button>
        <button type="button" className="cap-side" onClick={onClose} disabled={busy}>
          {t("intake.capture.done")}
        </button>
        <input ref={input} type="file" accept="image/*" capture="environment" hidden aria-label={t("intake.capture.take")} onChange={(e) => { void take(e.target.files); e.target.value = ""; }} />
      </footer>
    </div>
  );
}

/** The base views still missing, under the grid (red in required mode; the count is under the Save button). */
export function IntakeStatus({ shots, required }: { shots: IntakeShot[]; required: boolean }) {
  const { t } = useTranslation();
  const missing = missingBaseViews(shots.map((s) => s.view));
  return (
    <>
      {missing.length > 0 && (
        <p className={required ? "missing" : "muted"} role="status">
          {t("intake.stillMissing", { views: missing.map((v) => t(`intake.view.${v}`)).join(", ") })}
        </p>
      )}
    </>
  );
}

/** The step "Átvétel fotókkal" of a new work order: the photos stay on the phone until the work order is saved. */
export interface LocalShot extends IntakeShot {
  blob: Blob;
  width: number;
  height: number;
}

export function IntakeStep({ no, shots, setShots }: { no: number; shots: LocalShot[]; setShots: (fn: (s: LocalShot[]) => LocalShot[]) => void }) {
  const { t } = useTranslation();
  const { workshop } = useApp();
  const s = intakeSettings(workshop.general);
  const [capture, setCapture] = useState<{ start?: IntakeView } | null>(null);
  const [open, setOpen] = useState<IntakeShot | null>(null);
  const label = useShotLabel();
  const add = async (view: IntakeView, file: File) => {
    const image = await shrinkPhoto(file);
    const url = typeof URL.createObjectURL === "function" ? URL.createObjectURL(image.blob) : "";
    setShots((list) => [...list, { id: `local-${Date.now()}-${list.length}`, view, url, createdAt: new Date(Date.now() + list.length).toISOString(), ...image }]);
  };
  return (
    <div className="intake-step">
      <h2>
        <span className="step-no">{no}</span> {t("intake.stepTitle")} <Help id="intake.step" /> <PlanTag featureKey="intakePhotos" />
      </h2>
      <IntakeGrid shots={shots} onStart={(v) => setCapture({ start: v })} onOpen={setOpen} />
      <button type="button" className="primary big-action" onClick={() => setCapture({})}>
        <Icon name="photo" size={28} />
        {t(shots.length === 0 ? "intake.start" : "intake.continue")}
      </button>
      <IntakeStatus shots={shots} required={s.mode === "required"} />
      {capture && <GuidedCapture shots={shots} start={capture.start} onShot={add} onClose={() => setCapture(null)} />}
      {open && (
        <ShotViewer
          label={label(shots, open)}
          url={open.url}
          onRemove={() => {
            setShots((list) => list.filter((x) => x.id !== open.id));
            setOpen(null);
          }}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}

export function ShotViewer({ label, url, onRemove, onClose }: { label: string; url: string; onRemove?: () => void; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="viewer" role="dialog" aria-modal="true" aria-label={label} onClick={onClose}>
      <div className="viewer-box" onClick={(e) => e.stopPropagation()}>
        <img src={url} alt={label} />
        <div className="viewer-bar">
          <strong>{label}</strong>
          <span className="viewer-actions">
            {onRemove && (
              <button type="button" className="danger" onClick={() => globalThis.confirm?.(t("photos.confirmDelete")) && onRemove()}>
                {t("common.delete")}
              </button>
            )}
            <button type="button" className="primary" onClick={onClose}>
              {t("common.close")}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * What was left in the car, as typed (the number of keys stays text until it is valid).
 * Part of the "intakePhotos" feature: on the intake step of a new work order and on the open work order.
 */
export interface IntakeExtrasDraft {
  left: LeftInCar[];
  keys: string;
  other: string;
}

export const draftOf = (x: IntakeExtras | null | undefined): IntakeExtrasDraft => ({
  left: [...(x?.left ?? [])], keys: x?.keys ? String(x.keys) : "", other: x?.other ?? "",
});

/** The stored form of a draft: null when nothing is given, undefined while the number of keys is not valid. */
export function extrasOfDraft(d: IntakeExtrasDraft): IntakeExtras | null | undefined {
  const keys = parseKeyCount(d.keys);
  if (keys === undefined) return undefined;
  return cleanIntakeExtras({ left: d.left, keys, other: d.other });
}

/** The "Autóban hagyva" ticks, the number of keys and a short other text. */
export function IntakeExtrasFields({ value, onChange }: { value: IntakeExtrasDraft; onChange: (d: IntakeExtrasDraft) => void }) {
  const { t } = useTranslation();
  const keysBad = parseKeyCount(value.keys) === undefined;
  return (
    <div className="ix-fields">
      <fieldset className="ix-left">
        <legend>
          {t("intake.left.label")} <Help id="intake.left" />
        </legend>
        {LEFT_IN_CAR.map((k) => (
          <label key={k} className={`choice-card ix-tick${value.left.includes(k) ? " selected" : ""}`}>
            <input
              type="checkbox"
              checked={value.left.includes(k)}
              onChange={(e) => onChange({ ...value, left: e.target.checked ? [...value.left, k] : value.left.filter((x) => x !== k) })}
            />
            <span>{t(`intake.left.${k}`)}</span>
          </label>
        ))}
        <label className="field ix-keys">
          <span className="field-label">{t("intake.left.keys")}</span>
          <input inputMode="numeric" value={value.keys} maxLength={2} aria-invalid={keysBad || undefined} aria-label={t("intake.left.keys")} onChange={(e) => onChange({ ...value, keys: e.target.value })} />
          {keysBad && <span className="missing">{t("intake.left.keysInvalid")}</span>}
        </label>
        <label className="field ix-other">
          <span className="field-label">{t("intake.left.other")}</span>
          <input value={value.other} maxLength={INTAKE_OTHER_MAX} aria-label={t("intake.left.other")} onChange={(e) => onChange({ ...value, other: e.target.value })} />
        </label>
      </fieldset>
    </div>
  );
}

/** The extras as label and text pairs, only what was given (work order facts, printed receipt, customer's page). */
export function useExtrasRows() {
  const { t } = useTranslation();
  return (x: IntakeExtras | null | undefined): [string, string][] => {
    const e = cleanIntakeExtras(x);
    if (!e) return [];
    const left = [...e.left.map((k) => t(`intake.left.${k}`)), ...(e.keys ? [t("intake.left.keysValue", { count: e.keys })] : []), ...(e.other ? [`${t("intake.left.other")}: ${e.other}`] : [])];
    return [
      ...(left.length > 0 ? [[t("intake.left.label"), left.join(", ")] as [string, string]] : []),
    ];
  };
}

/** The extras on the printed intake receipt (nothing when nothing was given). */
export function PrintIntakeExtras({ extras }: { extras: IntakeExtras | null | undefined }) {
  const { t } = useTranslation();
  const rows = useExtrasRows()(extras);
  if (rows.length === 0) return null;
  return (
    <section className="ps-text ps-extras">
      <p className="ps-cap">{t("intake.extras.print")}</p>
      <KeyValues rows={rows} />
    </section>
  );
}

/** What the customer signs, from the work order as it is now. A new signature uses the newest content version;
 * checking an older signature passes its own version, so it keeps matching. */
export function receiptContent(kind: SignatureKind, order: WorkOrder, customer: Customer | null, shots: IntakeShot[], signerName: string, signedAt: string, version = SIGNED_CONTENT_VERSION): SignedContent {
  return signedContent({
    kind,
    number: order.number,
    plate: order.plateNumber ?? "",
    km: order.odometerKm ?? null,
    customerName: customer?.name ?? order.customerName ?? "",
    request: order.customerRequest ?? "",
    expectedReadyAt: order.expectedReadyAt ?? null,
    photoIds: shots.map((s) => s.id),
    signedAt,
    signerName,
    ...(kind === "handover" ? { gross: order.gross ?? 0 } : {}),
    ...(version >= 2 ? { v: version, extras: order.intakeExtras ?? null } : {}),
  });
}

/** Whether the receipt still matches what was signed (photos, km, request ... unchanged); null while checking. */
export function useSignatureMatches(sig: WorkOrderSignature | undefined, order: WorkOrder, customer: Customer | null, shots: IntakeShot[]): boolean | null {
  const [ok, setOk] = useState<boolean | null>(null);
  // checked with the version it was signed with (version 1 has no `v` and no extras)
  const key = sig ? JSON.stringify(receiptContent(sig.kind, order, customer, shots, sig.content.signerName, sig.content.signedAt, sig.content.v ?? 1)) : "";
  useEffect(() => {
    if (!sig) return setOk(null);
    let live = true;
    void contentHash(JSON.parse(key) as SignedContent).then((h) => live && setOk(h === sig.contentHash));
    return () => {
      live = false;
    };
  }, [key, sig]);
  return ok;
}

/**
 * The signature pad: full screen, meant for landscape (it asks to turn the phone, works in portrait too).
 * The strokes are kept in relative coordinates, so turning the phone redraws them at the new size.
 */
export function SignaturePad({ title, statement, signerName, onSign, onClose }: {
  title: string;
  statement: string;
  signerName: string;
  onSign: (image: Blob, signerName: string) => Promise<void>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const canvas = useRef<HTMLCanvasElement>(null);
  const strokes = useRef<[number, number][][]>([]);
  const drawing = useRef(false);
  const [count, setCount] = useState(0);
  const [name, setName] = useState(signerName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [portrait, setPortrait] = useState(false);

  const redraw = useCallback(() => {
    const c = canvas.current;
    if (!c) return;
    const r = c.getBoundingClientRect();
    const dpr = globalThis.devicePixelRatio || 1;
    c.width = Math.max(1, Math.round(r.width * dpr));
    c.height = Math.max(1, Math.round(r.height * dpr));
    const g = c.getContext?.("2d");
    if (!g) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    paint(g, strokes.current, r.width, r.height, 3);
  }, []);
  useEffect(() => {
    redraw();
    const m = globalThis.matchMedia?.("(orientation: portrait)");
    const on = () => {
      setPortrait(!!m?.matches);
      redraw();
    };
    on();
    globalThis.addEventListener?.("resize", on);
    m?.addEventListener?.("change", on);
    return () => {
      globalThis.removeEventListener?.("resize", on);
      m?.removeEventListener?.("change", on);
    };
  }, [redraw]);

  const point = (e: React.PointerEvent<HTMLCanvasElement>): [number, number] => {
    const r = e.currentTarget.getBoundingClientRect();
    return [(e.clientX - r.left) / (r.width || 1), (e.clientY - r.top) / (r.height || 1)];
  };
  const down = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drawing.current = true;
    strokes.current.push([point(e)]);
    redraw();
  };
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    e.preventDefault();
    strokes.current[strokes.current.length - 1]!.push(point(e));
    redraw();
  };
  const up = () => {
    if (!drawing.current) return;
    drawing.current = false;
    setCount(strokes.current.filter((s) => s.length > 1).length);
  };
  const clear = () => {
    strokes.current = [];
    setCount(0);
    redraw();
  };
  const sign = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSign(await signatureImage(strokes.current), name.trim());
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };

  return (
    <div className="sig-pad" role="dialog" aria-modal="true" aria-label={title}>
      <header className="sig-head">
        <strong>{title}</strong>
        <button type="button" className="sig-close" onClick={onClose} disabled={busy}>
          {t("common.cancel")}
        </button>
      </header>
      <p className="sig-statement">{statement}</p>
      {portrait && <p className="sig-turn" role="status">{t("signature.turnPhone")}</p>}
      <div className="sig-area">
        <canvas
          ref={canvas}
          aria-label={t("signature.area")}
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerCancel={up}
          onPointerLeave={up}
        />
        <span className="sig-x" aria-hidden="true">×</span>
        <span className="sig-line" aria-hidden="true" />
        <label className="sig-name">
          <span>{t("signature.signerName")}</span>
          <input value={name} onChange={(e) => setName(e.target.value)} aria-label={t("signature.signerName")} />
        </label>
      </div>
      <ErrorText error={error} />
      <div className="sig-actions">
        <button type="button" onClick={clear} disabled={busy || count === 0}>
          {t("signature.clear")}
        </button>
        <button type="button" className="primary" onClick={() => void sign()} disabled={busy || count === 0 || !name.trim()}>
          {t("signature.sign")}
        </button>
      </div>
      {(count === 0 || !name.trim()) && <p className="missing sig-missing">{t(count === 0 ? "signature.needStroke" : "signature.needName")}</p>}
    </div>
  );
}

function paint(g: CanvasRenderingContext2D, strokes: [number, number][][], w: number, h: number, width: number) {
  g.clearRect(0, 0, w, h);
  g.strokeStyle = "#0E2433";
  g.lineWidth = width;
  g.lineCap = "round";
  g.lineJoin = "round";
  for (const s of strokes) {
    g.beginPath();
    s.forEach(([x, y], n) => (n === 0 ? g.moveTo(x * w, y * h) : g.lineTo(x * w, y * h)));
    if (s.length === 1) g.lineTo(s[0]![0] * w + 0.5, s[0]![1] * h);
    g.stroke();
  }
}

/** The signature as a PNG of 1200 x 480 px on white (prints well in black and white too). */
async function signatureImage(strokes: [number, number][][]): Promise<Blob> {
  const c = document.createElement("canvas");
  c.width = 1200;
  c.height = 480;
  const g = c.getContext?.("2d");
  if (g) {
    g.fillStyle = "#ffffff";
    g.fillRect(0, 0, c.width, c.height);
    paint(g, strokes, c.width, c.height, 7);
    // paint() clears to transparent first: put the white back under the ink
    g.globalCompositeOperation = "destination-over";
    g.fillStyle = "#ffffff";
    g.fillRect(0, 0, c.width, c.height);
  }
  const blob = await new Promise<Blob | null>((resolve) => (typeof c.toBlob === "function" ? c.toBlob(resolve, "image/png") : resolve(null)));
  // test environments without a canvas: an empty PNG stands in
  return blob ?? new Blob([new Uint8Array([137, 80, 78, 71])], { type: "image/png" });
}

/** The statement above the pad: plate, km, photo count, and what the signature means. */
export function useStatement() {
  const { t, i18n } = useTranslation();
  return (kind: SignatureKind, order: WorkOrder, photos: number) => {
    const plate = order.plateNumber ? formatPlate(order.plateNumber, order.plateCountry ?? "HU") : "";
    const km = order.odometerKm === null || order.odometerKm === undefined ? "–" : `${order.odometerKm.toLocaleString(i18n.language)} km`;
    return kind === "intake" ? t("signature.intakeStatement", { plate, km, count: photos }) : t("signature.handoverStatement", { plate, km });
  };
}

/** Signs on the phone and stores it; one button, the pad opens full screen. */
export function SignButton({ kind, order, customer, shots, label, autoOpen, onSigned, onCancel }: {
  kind: SignatureKind;
  order: WorkOrder;
  customer: Customer | null;
  shots: IntakeShot[];
  label: string;
  autoOpen?: boolean;
  onSigned: () => void;
  /** The pad was closed without signing. */
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const { store, workshop } = useApp();
  const [open, setOpen] = useState(!!autoOpen);
  useEffect(() => {
    if (autoOpen) setOpen(true);
  }, [autoOpen]);
  const statement = useStatement();
  const sign = async (image: Blob, signerName: string) => {
    const content = receiptContent(kind, order, customer, shots, signerName, new Date().toISOString());
    await store.addSignature(workshop.id, order.id, {
      kind, signerName, image, content, contentHash: await contentHash(content), userAgent: globalThis.navigator?.userAgent ?? null,
    });
    setOpen(false);
    onSigned();
  };
  return (
    <>
      <button type="button" className="primary big-action" onClick={() => setOpen(true)}>
        <Icon name="signature" size={28} />
        {label}
      </button>
      {open && (
        <SignaturePad
          title={t(kind === "intake" ? "signature.intakeTitle" : "signature.handoverTitle", { number: order.number })}
          statement={statement(kind, order, shots.length)}
          signerName={customer?.name ?? order.customerName ?? ""}
          onSign={sign}
          onClose={() => {
            setOpen(false);
            onCancel?.();
          }}
        />
      )}
    </>
  );
}

/** A signature given on the phone: who, when, the small image and whether the receipt still matches. */
export function SignedNote({ sig, matches }: { sig: WorkOrderSignature; matches: boolean | null }) {
  const { t, i18n } = useTranslation();
  const when = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(sig.signedAt));
  return (
    <div className="signed-note">
      {sig.imageUrl && <img src={sig.imageUrl} alt={t("signature.imageAlt", { name: sig.signerName })} />}
      <p className="ok-band">
        {t(sig.kind === "intake" ? "signature.signedIntake" : "signature.signedHandover", { name: sig.signerName, date: when })}{" "}
        <span className="mono small">{shortHash(sig.contentHash)}</span>
      </p>
      {matches === false && <p className="warn">{t("signature.changed")}</p>}
    </div>
  );
}

/** Fired when a customer's objection arrives, so an open work order reloads itself. */
export const INTAKE_OBJECTION_EVENT = "szerviz:intake-objection";

/** The intake e-mail in the workshop's words: formal address, the link, the phone number. */
export function useIntakeMessage() {
  const { t, i18n } = useTranslation();
  const { workshop } = useApp();
  /** `signed`: the customer signed the receipt on the phone (information only); otherwise the page lets them object. */
  return (order: WorkOrder, customerName: string | null, link: string, photos: number, signed: boolean) => {
    const g = (k: string) => (typeof workshop.general[k] === "string" ? String(workshop.general[k]).trim() : "");
    const date = new Intl.DateTimeFormat(i18n.language, { dateStyle: "long", timeStyle: "short" }).format(new Date(order.receivedAt));
    const plate = order.plateNumber ? formatPlate(order.plateNumber, order.plateCountry ?? "HU") : "";
    const text = [
      customerName ? t("intake.mail.greeting", { name: customerName }) : t("intake.mail.greetingNoName"),
      t(signed ? "intake.mail.bodySigned" : "intake.mail.bodyUnsigned", { plate, date, count: photos, link }),
      g("workshopPhone") ? t("intake.mail.call", { phone: g("workshopPhone") }) : "",
      [workshop.name, siteAddressOf(workshop.general) ?? "", g("workshopPhone"), g("workshopEmail")].filter(Boolean).join("\n"),
    ].filter(Boolean).join("\n\n");
    // the short SMS (channel ticks): the link and who sent it, within two SMS where the alphabet allows
    const sms = buildReadySms(t("intake.sms", { plate, count: photos, workshop: workshop.name }), { link }).text;
    return { subject: t("intake.mail.subject", { number: order.number }), text, link, buttonLabel: t("intake.mail.button"), sms };
  };
}

/**
 * The intake on the work order: the photos of the round (more can be added while it is open), the phone
 * signature, the customer's link and the customer's objections.
 */
export function IntakePanel({ order, customer, vehicle, photos, onPhotosChanged, autoSign, sendAfterSign, onSignaturesChanged, signatures, onLink }: {
  order: WorkOrder;
  customer: Customer | null;
  /** The work order's vehicle: its driver may get the intake information too (feature "vehicleDriver"). */
  vehicle?: Vehicle | null;
  photos: Photo[];
  onPhotosChanged: () => Promise<void>;
  autoSign?: boolean;
  /** The new work order's e-mail waits for the end of the signing step (signed or closed without signing). */
  sendAfterSign?: boolean;
  signatures: WorkOrderSignature[];
  /** The newest customer link after every load (the printed receipt shows it). */
  onLink?: (link: IntakeLink | null) => void;
  onSignaturesChanged: () => void;
}) {
  const canNotice = useCan("notice");
  const { t, i18n } = useTranslation();
  const { store, workshop } = useApp();
  const on = useFeature("intakePhotos");
  const signOn = useFeature("phoneSignature");
  const smsOn = useFeature("sms");
  const driverOn = useFeature("vehicleDriver");
  const s = intakeSettings(workshop.general);
  const closed = order.status === "closed";
  const shots = shotsOf(photos);
  const label = useShotLabel();
  const [capture, setCapture] = useState<{ start?: IntakeView } | null>(null);
  const [open, setOpen] = useState<IntakeShot | null>(null);
  const [link, setLink] = useState<IntakeLink | null>(null);
  const [objections, setObjections] = useState<IntakeObjection[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [sending, setSending] = useState(false);
  const message = useIntakeMessage();
  const sig = signatures.find((x) => x.kind === "intake");
  const matches = useSignatureMatches(sig, order, customer, shots);
  const pendingDone = useRef(false);
  const date = (iso: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));

  const load = useCallback(async () => {
    const l = await store.lastIntakeLink(order.id);
    setLink(l);
    onLink?.(l);
    setObjections(await store.intakeObjections(order.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, order.id]);
  useEffect(() => void load(), [load]);
  useEffect(() => {
    const again = () => void load();
    globalThis.addEventListener?.(INTAKE_OBJECTION_EVENT, again);
    return () => globalThis.removeEventListener?.(INTAKE_OBJECTION_EVENT, again);
  }, [load]);

  if (!on && shots.length === 0 && objections.length === 0 && !sig) return null;
  const add = async (view: IntakeView, file: File) => {
    const image = await shrinkPhoto(file);
    await store.addPhoto(workshop.id, { vehicleId: order.vehicleId ?? null, workOrderId: order.id, kind: "intake", intakeView: view }, image);
    await onPhotosChanged();
  };
  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e);
    }
    await load();
  };
  const send = (signed = !!sig) =>
    void run(async () => {
      setSending(true);
      try {
        const token = await store.createIntakeLink(order.id);
        await store.sendIntakeEmail(order.id, message(order, customer?.name ?? order.customerName ?? null, intakeLink(token), shots.length, signed));
      } finally {
        setSending(false);
      }
    });
  // the e-mail of a new work order, once, when its signing step ends
  const afterSign = (signed: boolean) => {
    if (!sendAfterSign || pendingDone.current) return;
    pendingDone.current = true;
    send(signed);
  };
  const linkWorks = link && link.expiresAt > new Date().toISOString();
  // the mechanic sends the link at the intake; sending it again is a customer notice (administrator, 2026-11-04)
  // the driver who brought the car may get it too (owner, 2026-11-05): both are offered, both ticked
  const driver = driverOn && vehicle && hasDriverContact(vehicle) ? vehicle : null;
  const driverContact = driver ? { email: driver.driverEmail ?? null, mobile: driver.driverMobile ?? null, phone: null } : null;
  const reachable = (c: { email?: string | null; mobile?: string | null; phone?: string | null } | null) => !!c?.email || (smsOn && !!smsNumberOf(c));
  const canSend = on && s.send === "ifEmail" && (reachable(customer) || reachable(driverContact)) && (!link?.sentAt || canNotice);

  return (
    <section className="intake-panel no-print" aria-label={t("intake.panelTitle")}>
      <h2>
        {t("intake.panelTitle")} <Help id="intake.panel" /> <PlanTag featureKey="intakePhotos" />
      </h2>
      {objections.filter((x) => !x.resolvedAt).map((x) => (
        <div key={x.id} className="objection" role="alert">
          <p>
            <strong>{t("intake.objection.title", { date: date(x.createdAt) })}</strong>
          </p>
          <p className="pre">{x.text}</p>
          <button type="button" onClick={() => void run(() => store.resolveIntakeObjection(x.id))}>
            {t("intake.objection.resolve")}
          </button>
        </div>
      ))}
      {objections.filter((x) => x.resolvedAt).map((x) => (
        <p key={x.id} className="muted">
          {t("intake.objection.resolved", { date: date(x.createdAt), text: x.text })}
        </p>
      ))}
      <IntakeGrid shots={shots} onStart={!closed && on ? (v) => setCapture({ start: v }) : undefined} onOpen={setOpen} />
      <p>{t("intake.count", { count: shots.length })}</p>
      {!closed && on && (
        <>
          <button type="button" className={shots.length === 0 ? "primary big-action" : "big-action"} onClick={() => setCapture({})}>
            <Icon name="photo" size={28} />
            {t(shots.length === 0 ? "intake.start" : "intake.continue")}
          </button>
          <IntakeStatus shots={shots} required={false} />
        </>
      )}
      {sig ? (
        <SignedNote sig={sig} matches={matches} />
      ) : (
        signOn && !closed && (
          <div className="sign-row">
            <SignButton
              kind="intake"
              order={order}
              customer={customer}
              shots={shots}
              label={t("signature.button")}
              autoOpen={autoSign}
              onSigned={() => {
                onSignaturesChanged();
                afterSign(true);
              }}
              onCancel={() => afterSign(false)}
            />
            <p className="muted">
              {t("signature.paperStays")} <Help id="signature.button" />
            </p>
            {/* at a computer the pad never opens by itself: the paper, or the mechanic's phone at the intake */}
            {deviceKind() !== "touch" && <p className="muted small">{t("signature.desktopNote")}</p>}
          </div>
        )
      )}
      {on && (
        <div className="intake-link">
          {link?.sentTo && link.sentAt ? (
            <p className={linkWorks ? "ok-band" : "muted"}>
              {t(linkWorks ? "intake.link.sent" : "intake.link.expired", { to: link.sentTo, date: date(link.sentAt) })}
            </p>
          ) : (
            <p className="muted">{t(s.send === "never" ? "intake.link.never" : customer?.email ? "intake.link.notSent" : "intake.link.noEmail")}</p>
          )}
          {canSend && shots.length > 0 && driver && (
            // the driver and the customer as two cards; one link for both (its objection works for whoever got it)
            <RecipientsSend
              type="intake"
              targets={recipientDefaults("intake", true).map(({ key, ticked }) =>
                key === "driver" ? { key, ticked, name: driver.driverName ?? null, contact: driverContact } : { key, ticked, name: customer?.name ?? null, contact: customer })}
              channels={smsOn ? ["email", "sms"] : ["email"]}
              primary={false}
              buttonLabel={t(link?.sentAt ? "intake.link.resendChannels" : "intake.link.sendChannels")}
              shared
              prepare={() => store.createIntakeLink(order.id)}
              send={(recipient, channel, token) => {
                const name = recipient === "driver" ? (driver.driverName ?? null) : (customer?.name ?? order.customerName ?? null);
                const m = message(order, name, intakeLink(token as string), shots.length, !!sig);
                return channel === "email"
                  ? store.sendIntakeEmail(order.id, { subject: m.subject, text: m.text, link: m.link, buttonLabel: m.buttonLabel }, recipient)
                  : store.sendIntakeSms(order.id, { subject: "", text: m.sms, link: m.link, buttonLabel: "" }, recipient);
              }}
              onDone={() => load()}
            />
          )}
          {canSend && shots.length > 0 && !driver && (
            // channel ticks like every customer message (owner, 2026-11-04): e-mail, and SMS when the SMS option is on
            <ChannelSend
              type="intake"
              customer={customer}
              channels={smsOn ? ["email", "sms"] : ["email"]}
              primary={false}
              buttonLabel={t(link?.sentAt ? "intake.link.resendChannels" : "intake.link.sendChannels")}
              prepare={() => store.createIntakeLink(order.id)}
              send={(channel, token) => {
                const m = message(order, customer?.name ?? order.customerName ?? null, intakeLink(token as string), shots.length, !!sig);
                return channel === "email"
                  ? store.sendIntakeEmail(order.id, { subject: m.subject, text: m.text, link: m.link, buttonLabel: m.buttonLabel })
                  : store.sendIntakeSms(order.id, { subject: "", text: m.sms, link: m.link, buttonLabel: "" });
              }}
              onDone={() => load()}
            />
          )}
        </div>
      )}
      <ErrorText error={error} />
      {capture && <GuidedCapture shots={shots} start={capture.start} onShot={add} onClose={() => setCapture(null)} />}
      {open && (
        <ShotViewer
          label={label(shots, open)}
          url={open.url}
          onRemove={closed ? undefined : () => void run(async () => {
            await store.deletePhoto(open.id);
            setOpen(null);
            await onPhotosChanged();
          })}
          onClose={() => setOpen(null)}
        />
      )}
    </section>
  );
}

/** The workshop's choices for the intake (under the feature's card in Settings > Options). */
export function IntakeSettings({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const { store, workshop, reloadWorkshop } = useApp();
  const s = intakeSettings(workshop.general);
  const [min, setMin] = useState(String(s.min));
  const [error, setError] = useState<unknown>(null);
  const save = async (patch: Record<string, unknown>) => {
    setError(null);
    try {
      await store.saveGeneralSettings(workshop.id, { ...workshop.general, ...patch } as Record<string, number | string | boolean | null>);
      await reloadWorkshop();
    } catch (e) {
      setError(e);
    }
  };
  const minOk = /^\d{1,2}$/.test(min.trim()) && Number(min) >= 1 && Number(min) <= 20;
  const cards = (key: string, value: string, options: readonly string[], help: string) => (
    <fieldset className="choice-cards" disabled={!canEdit}>
      <legend>
        {t(`setting.${key}.label`)} <Help id={help} />
      </legend>
      {options.map((o) => (
        <label key={o} className={`choice-card${value === o ? " selected" : ""}`}>
          <input type="radio" name={key} value={o} checked={value === o} onChange={() => void save({ [key]: o })} />
          <span>{t(`setting.${key}.option.${o}`)}</span>
        </label>
      ))}
    </fieldset>
  );
  return (
    <div className="feature-settings" aria-label={t("intake.settingsTitle")}>
      {cards("intakePhotoMode", s.mode, ["offer", "required"], "intake.mode")}
      {s.mode === "required" && (
        <div className="actions intake-min">
          <label className="field">
            <span className="field-label">
              {t("setting.intakePhotoMin.label")} <Help id="intake.min" />
            </span>
            <input inputMode="numeric" value={min} disabled={!canEdit} aria-label={t("setting.intakePhotoMin.label")} aria-invalid={!minOk || undefined} onChange={(e) => setMin(e.target.value)} />
          </label>
          {canEdit && (
            <button type="button" disabled={!minOk || Number(min) === s.min} onClick={() => void save({ intakePhotoMin: Number(min) })}>
              {t("common.save")}
            </button>
          )}
          {!minOk && <span className="missing">{t("intake.minInvalid")}</span>}
        </div>
      )}
      {cards("intakePhotoSend", s.send, ["ifEmail", "never"], "intake.send")}
      {cards("intakePhotoRetention", s.retention, ["warrantyPlus1y", "5y"], "intake.retention")}
      <ErrorText error={error} />
    </div>
  );
}

/** The corner notice when a customer says "Nem értek egyet" on the intake link (like the quote answers). */
export function IntakeNotices() {
  const { t } = useTranslation();
  const { store, workshop, go } = useApp();
  const on = useFeature("intakePhotos");
  const [notices, setNotices] = useState<IntakeObjectionNotice[]>([]);
  const key = `szerviz.intakeSeen.${workshop.id}`;
  const since = useRef<string>(readKey(key) ?? new Date().toISOString());
  const shown = useRef(new Set<string>());
  const check = useCallback(async () => {
    try {
      const fresh = (await store.recentIntakeObjections(workshop.id, since.current)).filter((n) => !shown.current.has(n.id));
      if (fresh.length === 0) return;
      for (const n of fresh) shown.current.add(n.id);
      setNotices((list) => [...fresh, ...list]);
      globalThis.dispatchEvent?.(new Event(INTAKE_OBJECTION_EVENT));
    } catch {
      // offline for a moment: next time
    }
  }, [store, workshop.id]);
  useEffect(() => {
    if (!on) return;
    if (!readKey(key)) writeKey(key, since.current);
    void check();
    const timer = setInterval(() => void check(), 30_000);
    const onVisible = () => globalThis.document?.visibilityState !== "hidden" && void check();
    globalThis.addEventListener?.("focus", onVisible);
    return () => {
      clearInterval(timer);
      globalThis.removeEventListener?.("focus", onVisible);
    };
  }, [on, check, key]);
  const dismiss = (n: IntakeObjectionNotice) =>
    setNotices((list) => {
      const rest = list.filter((x) => x.id !== n.id);
      if (rest.length === 0) {
        since.current = [n, ...list].map((x) => x.createdAt).sort().at(-1)!;
        writeKey(key, since.current);
      }
      return rest;
    });
  if (!on || notices.length === 0) return null;
  return (
    <div className="notices notices-intake" aria-live="polite">
      {notices.map((n) => (
        <div key={n.id} className="notice none" role="status">
          <p>
            {t("intake.notice", {
              customer: n.customerName ?? t("quote.notice.someone"),
              plate: n.plateNumber ? formatPlate(n.plateNumber, n.plateCountry ?? "HU") : n.number,
              text: n.text.length > 120 ? `${n.text.slice(0, 117)}…` : n.text,
            })}
          </p>
          <div className="actions">
            <button type="button" className="primary" onClick={() => { dismiss(n); go("workOrders", n.workOrderId); }}>
              {t("quote.notice.open")}
            </button>
            <button type="button" onClick={() => dismiss(n)}>
              {t("quote.notice.close")}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function readKey(k: string): string | null {
  try {
    return globalThis.localStorage?.getItem(k) ?? null;
  } catch {
    return null;
  }
}
function writeKey(k: string, v: string) {
  try {
    globalThis.localStorage?.setItem(k, v);
  } catch {
    // storage unavailable: the notice may show again after a reload
  }
}

/** The objection box of the customer's page (only without a phone signature). */
export function ObjectionBox({ onSend }: { onSend: (text: string) => Promise<void> }) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const clean = cleanObjection(text);
  return (
    <section className="ip-object" aria-label={t("intakePage.objectTitle")}>
      <h2>{t("intakePage.objectTitle")}</h2>
      <p>{t("intakePage.objectText")}</p>
      <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} aria-label={t("intakePage.objectTitle")} maxLength={2000} />
      <ErrorText error={error} />
      <button
        type="button"
        className="ip-object-btn"
        disabled={!clean || busy}
        onClick={() => {
          if (!clean) return;
          setBusy(true);
          setError(null);
          onSend(clean).catch((e) => {
            setError(e);
            setBusy(false);
          });
        }}
      >
        {t("intakePage.objectButton")}
      </button>
    </section>
  );
}

/** The small time on a thumbnail: day, month and time ("11. 04. 10:32" in the page's language). */
export function shotTime(iso: string, language: string): string {
  return new Intl.DateTimeFormat(language, { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
}

/** Thumbnails with view labels and the time of each photo for the printed receipt (at most 8, "+N a linken" for the rest). */
export function PrintCollage({ shots, more, caption }: { shots: IntakeShot[]; more: number; caption: ReactNode }) {
  const { t, i18n } = useTranslation();
  const label = useShotLabel();
  return (
    <section className="ps-collage">
      <p className="ps-cap">{t("intake.print.title")}</p>
      <div className="ps-collage-grid">
        {shots.map((s) => (
          <figure key={s.id}>
            <img src={s.url} alt="" />
            <time className="ps-collage-time" dateTime={s.createdAt}>{shotTime(s.createdAt, i18n.language)}</time>
            <figcaption>{label(shots, s)}</figcaption>
          </figure>
        ))}
        {more > 0 && (
          <figure className="ps-collage-more">
            <span>{t("intake.print.more", { count: more })}</span>
          </figure>
        )}
      </div>
      <p className="ps-collage-caption">{caption}</p>
    </section>
  );
}

const parseKm = (s: string): number | null => {
  const v = s.replace(/\s/g, "").replace(",", ".");
  if (v === "") return null;
  const n = Number(v);
  return odometerFromInput(Number.isFinite(n) ? n : null);
};

/**
 * "Átvétel megkezdése" (owner, 2026-11-04): the intake the office handed to the mechanic, step by step on the
 * phone: the odometer reading (required, the lower-than-last warning when the field is left), the intake photos
 * (as configured; uploaded at once), what was left in the car, then "Átvétel kész". With the phone signature on,
 * the signing pad follows (on a touch device by itself). The database checks the same (work_orders_intake_guard).
 */
export function IntakeWalk({ order, photos, onPhotosChanged, onFinished, onCancel }: {
  order: WorkOrder;
  photos: Photo[];
  onPhotosChanged: () => Promise<void>;
  onFinished: () => Promise<void> | void;
  onCancel: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { store, workshop } = useApp();
  const on = useFeature("intakePhotos");
  const signOn = useFeature("phoneSignature");
  const s = intakeSettings(workshop.general);
  const shots = shotsOf(photos);
  const label = useShotLabel();
  const [km, setKm] = useState("");
  const [left, setLeft] = useState<string | null>(null);
  const [lastKm, setLastKm] = useState<number | null>(null);
  const [extras, setExtras] = useState<IntakeExtrasDraft>(() => draftOf(order.intakeExtras));
  const [capture, setCapture] = useState<{ start?: IntakeView } | null>(null);
  const [open, setOpen] = useState<IntakeShot | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    if (order.vehicleId) void store.lastOdometer(order.vehicleId).then(setLastKm, () => setLastKm(null));
  }, [store, order.vehicleId]);
  const kmValue = parseKm(km);
  const warning = left !== null && left === km ? odometerWarning(kmValue, lastKm) : null;
  const extrasValue = on ? extrasOfDraft(extras) : null;
  const needs = intakeNeeds({ odometerKm: kmValue }, s, shots.length, on);
  const add = async (view: IntakeView, file: File) => {
    const image = await shrinkPhoto(file);
    await store.addPhoto(workshop.id, { vehicleId: order.vehicleId ?? null, workOrderId: order.id, kind: "intake", intakeView: view }, image);
    await onPhotosChanged();
  };
  const finish = async () => {
    if (kmValue === null || needs.length > 0 || extrasValue === undefined) return;
    setBusy(true);
    setError(null);
    try {
      await store.finishIntake(order.id, { odometerKm: kmValue, ...(on ? { intakeExtras: extrasValue } : {}) });
      await onFinished();
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };
  let no = 0;
  return (
    <section className="intake-walk" aria-label={t("intakeWalk.heading")}>
      <h2>
        {t("intakeWalk.heading")} <Help id="intakeWalk.start" />
      </h2>
      <h3>
        <span className="step-no">{++no}</span> {t("workOrder.odometerKm")}
      </h3>
      <div className="form-grid">
        <TextInput label={t("workOrder.odometerKm")} value={km} onChange={setKm} onBlur={() => setLeft(km)} required autoFocus
          invalid={!!km && kmValue === null} help="workOrder.odometerKm" />
        {warning && (
          <p className="warn wide" role="status">
            {t("workOrder.odometerLower", { last: lastKm === null ? "" : lastKm.toLocaleString(i18n.language) })}
          </p>
        )}
      </div>
      {on && (
        <>
          <h3>
            <span className="step-no">{++no}</span> {t("intake.stepTitle")} <PlanTag featureKey="intakePhotos" />
          </h3>
          <IntakeGrid shots={shots} onStart={(v) => setCapture({ start: v })} onOpen={setOpen} />
          <button type="button" className={shots.length === 0 ? "primary big-action" : "big-action"} onClick={() => setCapture({})}>
            <Icon name="photo" size={28} />
            {t(shots.length === 0 ? "intake.start" : "intake.continue")}
          </button>
          <IntakeStatus shots={shots} required={s.mode === "required"} />
          <h3>
            <span className="step-no">{++no}</span> {t("intake.left.label")}
          </h3>
          <IntakeExtrasFields value={extras} onChange={setExtras} />
        </>
      )}
      {signOn && <p className="muted">{t("intakeWalk.signNext")}</p>}
      <div className="actions">
        <ErrorText error={error} />
        <button type="button" className="primary big-action" disabled={needs.length > 0 || extrasValue === undefined || busy} onClick={() => void finish()}>
          {t("intakeWalk.done")}
        </button>
        <button type="button" onClick={onCancel} disabled={busy}>
          {t("common.cancel")}
        </button>
        {needs.length > 0 && (
          <p className="missing" role="status">
            {t("intakeWalk.missing", { what: needs.map((n) => t(`intakeWalk.need.${n}`, { min: s.min, done: shots.length })).join(", ") })}
          </p>
        )}
        {extrasValue === undefined && <p className="missing" role="status">{t("intake.left.keys")}: {t("intake.left.keysInvalid")}</p>}
      </div>
      {capture && <GuidedCapture shots={shots} start={capture.start} onShot={add} onClose={() => setCapture(null)} />}
      {open && (
        <ShotViewer
          label={label(shots, open)}
          url={open.url}
          onRemove={() => void store.deletePhoto(open.id).then(async () => { setOpen(null); await onPhotosChanged(); }, setError)}
          onClose={() => setOpen(null)}
        />
      )}
    </section>
  );
}

/** The phone address the QR code of the printed receipt opens: this work order, at the photo of the signed paper. */
export const paperLink = (workOrderId: string) =>
  `${globalThis.location?.origin ?? ""}${globalThis.location?.pathname ?? "/"}#workOrders/${workOrderId}/paper`;

/**
 * The intake receipt signed on paper (owner, 2026-11-04): "Az ügyfél papíron aláírta" records it (when and who);
 * with the workshop setting "intakePaperPhoto" a photo or scan of the signed paper can be added too ("Aláírt papír
 * fotója"), also after closing. Shown unless the customer signed on the phone.
 */
export function PaperSignature({ order, photos, onChanged, jump, recorderName }: {
  order: WorkOrder;
  /** All photos of the work order (the signed paper photos are among them). */
  photos: Photo[];
  onChanged: () => Promise<void> | void;
  /** Opened from the QR code of the printed receipt: scrolled into view and highlighted. */
  jump?: boolean;
  /** Who recorded the paper signature (member name). */
  recorderName: string | null;
}) {
  const { t, i18n } = useTranslation();
  const { store, workshop } = useApp();
  const canUndo = useCan("close");
  const photoOn = workshop.general.intakePaperPhoto === true;
  const papers = photos.filter((p) => p.docType === "signedIntake");
  const input = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [view, setView] = useState<Photo | null>(null);
  useEffect(() => {
    if (jump) box.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  }, [jump]);
  const when = (iso: string) => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await onChanged();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const upload = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    void run(async () => {
      const image = await shrinkPhoto(file);
      await store.addPhoto(workshop.id, { vehicleId: order.vehicleId ?? null, workOrderId: order.id, kind: "document", docType: "signedIntake", caption: t("paper.photoAlt") }, image);
    });
  };
  const signed = !!order.paperSigned || papers.length > 0;
  return (
    <section ref={box} className={`paper-sign no-print${jump ? " paper-jump" : ""}`} aria-label={t("paper.title")}>
      <h2>
        {t("paper.title")} <Help id="paper.record" />
      </h2>
      {signed ? (
        <p className="ok-band" role="status">
          {order.paperSigned
            ? t("paper.recorded", { date: when(order.paperSigned.at), name: recorderName ?? "–" })
            : t("paper.photoOnly")}
        </p>
      ) : (
        <p className="muted">{t("paper.text")}</p>
      )}
      {papers.length > 0 && (
        <ul className="paper-thumbs">
          {papers.map((p) => (
            <li key={p.id}>
              <button type="button" onClick={() => setView(p)} aria-label={t("paper.photoAlt")}>
                <img src={p.url} alt={t("paper.photoAlt")} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {jump && photoOn && papers.length === 0 && <p className="warn" role="status">{t("paper.jump")}</p>}
      <div className="actions">
        {!order.paperSigned && (
          <button type="button" className={signed ? "" : "primary"} disabled={busy} onClick={() => void run(() => store.recordPaperSignature(workshop.id, order.id))}>
            {t("paper.record")}
          </button>
        )}
        {photoOn && (
          <>
            <button type="button" className="big-action" disabled={busy} onClick={() => input.current?.click()}>
              <Icon name="photo" size={28} />
              {t("paper.photo")}
            </button>
            <input ref={input} type="file" accept="image/*,application/pdf" capture="environment" hidden aria-label={t("paper.photo")}
              onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
          </>
        )}
        {order.paperSigned && canUndo && (
          <button type="button" className="link" disabled={busy} onClick={() => globalThis.confirm?.(t("paper.undoConfirm")) && void run(() => store.deletePaperSignature(order.id))}>
            {t("paper.undo")}
          </button>
        )}
      </div>
      <ErrorText error={error} />
      {view && <ShotViewer label={t("paper.photoAlt")} url={view.url} onClose={() => setView(null)} />}
    </section>
  );
}
